import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useSelector } from "react-redux";
import { Card, CardHeader } from "src/components/commons/Card";
import Table from "src/components/commons/Table/Table";
import StatusChip from "src/components/commons/StatusChip";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import Box from "@material-ui/core/Box";
import Typography from "@material-ui/core/Typography";
import Dialog from "@material-ui/core/Dialog";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContent from "@material-ui/core/DialogContent";
import DialogActions from "@material-ui/core/DialogActions";
import CircularProgress from "@material-ui/core/CircularProgress";
import DownloadIcon from "@material-ui/icons/GetApp";
import SearchIcon from "@material-ui/icons/Search";
import { getFunderOutreachData } from "src/api/outreach";
import { concatenateNames, formatDate, getFunderInfo } from "src/helpers/outreachHelpers";
import { exportTableToCSV } from "src/helpers/csvExport";
import { format } from "date-fns";

const FunderOutreach = () => {
  const { tenant, authUser } = useSelector(({ auth }) => auth);
  const [outreachData, setOutreachData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [selectedRow, setSelectedRow] = useState(null);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  
  // Separate local filter input state from API filter state
  const [filterInputs, setFilterInputs] = useState({
    campaign: "",
    funder: "",
    status: "",
  });
  
  // API filter state (debounced)
  const [apiFilters, setApiFilters] = useState({
    campaign: "",
    funder: "",
    status: "",
  });

  // Debounced effect for API filters - optimized to prevent unnecessary re-renders
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setApiFilters(filterInputs);
      setPage(0); // Reset to first page when filters change
    }, 500); // 500ms debounce

    return () => clearTimeout(timeoutId);
  }, [filterInputs]);

  // Fetch outreach data - memoized to prevent unnecessary API calls
  const fetchOutreachData = useCallback(async () => {
    try {
      setLoading(true);
      
      const response = await getFunderOutreachData({
        page: page + 1,
        limit: rowsPerPage,
        ...apiFilters,
      }, authUser);
      
      // Handle both response formats: direct array or {data: [], total: number}
      const responseData = Array.isArray(response) ? response : (response?.data || []);
      const responseTotal = response?.total || responseData.length;
      
      setOutreachData(responseData);
      setTotalCount(responseTotal);
      setError(null);
      
    } catch (err) {
      console.error("Error fetching outreach data:", err);
      setError("Failed to load outreach data");
      setOutreachData([]);
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, apiFilters, authUser]);

  // Fetch outreach data effect
  useEffect(() => {
    fetchOutreachData();
  }, [fetchOutreachData]);

  // Handle row click to view details
  const handleRowClick = useCallback((row) => {
    setSelectedRow(row);
    setViewDialogOpen(true);
  }, []);



  // Handle filter input changes (local state only) - memoized to prevent re-creation
  const handleFilterInputChange = useCallback((field, value) => {
    setFilterInputs(prev => ({ ...prev, [field]: value }));
  }, []);

  // Handle filter clear
  const handleClearFilters = useCallback(() => {
    setFilterInputs({ campaign: "", funder: "", status: "" });
    // apiFilters will be updated via useEffect debounce
  }, []);

  // Calculate overview metrics - memoized to prevent recalculation on every render
  const overviewMetrics = useMemo(() => {
    if (outreachData.length === 0) {
      return {
        totalCampaigns: 0,
        totalOutreach: 0,
        pendingOutreach: 0,
        outreachSent: 0,
        responsesReceived: 0,
        successfulConnections: 0,
        unresponsive: 0,
      };
    }

    const uniqueCampaignIds = new Set(outreachData.map(row => row.campaign_id));
    
    return {
      totalCampaigns: uniqueCampaignIds.size,
      totalOutreach: totalCount,
      pendingOutreach: outreachData.filter(row => row.status === "PENDING_OUTREACH").length,
      outreachSent: outreachData.filter(row => row.status === "OUTREACH_SENT").length,
      responsesReceived: outreachData.filter(row => row.status === "ONBOARDED").length,
      successfulConnections: outreachData.filter(row => row.status === "CONNECTED").length,
      unresponsive: outreachData.filter(row => row.status === "UNRESPONSIVE").length,
    };
  }, [outreachData, totalCount]);

  // Table columns configuration - memoized to prevent re-creation
  const columns = useMemo(() => [
    {
      header: "Campaign",
      accessor: "campaign_name",
      align: "left",
      render: (value, row) => (
        <Box>
          <Typography variant="body2" style={{ fontWeight: 500 }}>
            {value || "N/A"}
          </Typography>
          <Typography variant="caption" color="textSecondary">
            ID: {row.campaign_id}
          </Typography>
        </Box>
      ),
    },
    {
      header: "Founder",
      accessor: "founder_name",
      align: "left",
      render: (value, row) => (
        <Box>
          <Typography variant="body2">{value || "N/A"}</Typography>
          <Typography variant="caption" color="textSecondary">
            {row.founder_email || "N/A"}
          </Typography>
        </Box>
      ),
    },
    {
      header: "Funder",
      accessor: "funder_name",
      align: "left",
      render: (value, row) => {
        // Use the helper function to get funder info from payload if JOIN failed
        const funderInfo = getFunderInfo(row);
        return (
          <Box>
            <Typography variant="body2">{funderInfo.name}</Typography>
            <Typography variant="caption" color="textSecondary">
              Score: {row.score || "N/A"}
            </Typography>
            {funderInfo.email && (
              <Typography variant="caption" color="textSecondary" display="block">
                {funderInfo.email}
              </Typography>
            )}
          </Box>
        );
      },
    },
    {
      header: "Status",
      accessor: "status",
      align: "center",
      render: (value) => <StatusChip status={value} />,
    },
    {
      header: "Outreach Date",
      accessor: "outreach_sent_at",
      align: "center",
      render: (value) => value ? format(new Date(value), "MMM dd, yyyy") : "Not sent",
    },
    {
      header: "Last Activity",
      accessor: "updated_at",
      align: "center",
      render: (value) => value ? format(new Date(value), "MMM dd, yyyy") : "N/A",
    },
  ], []);

  // Handle CSV export - defined after columns to avoid hoisting issues
  const handleExportCSV = useCallback(() => {
    try {
      // Use the dynamic CSV export helper
      exportTableToCSV(
        columns, // Use the table columns configuration
        outreachData, // Use the current table data
        `funder-outreach-${format(new Date(), "yyyy-MM-dd")}` // Filename
      );
    } catch (error) {
      console.error('Error exporting CSV:', error);
      // You could add error handling here if needed
    }
  }, [columns, outreachData]);

  // Filter section - memoized to prevent re-creation
  const FilterSection = useMemo(() => (
    <Card className="mb-4">
      <CardHeader>
        <span className="text-lg">Filters</span>
        <div className="flex items-center gap-2">
          {filterInputs.campaign || filterInputs.funder || filterInputs.status ? (
            <Typography variant="caption" color="primary">
              Filters applied
            </Typography>
          ) : null}
          <Button
            variant="outlined"
            size="small"
            onClick={handleClearFilters}
            disabled={!filterInputs.campaign && !filterInputs.funder && !filterInputs.status}
          >
            Clear Filters
          </Button>
        </div>
      </CardHeader>
      <div className="p-4 grid grid-cols-1 md:grid-cols-3 gap-4">
        <TextField
          label="Campaign Name"
          value={filterInputs.campaign}
          onChange={(e) => handleFilterInputChange("campaign", e.target.value)}
          variant="outlined"
          size="small"
          fullWidth
          placeholder="Type to search campaigns..."
        />
        <TextField
          label="Funder Name"
          value={filterInputs.funder}
          onChange={(e) => handleFilterInputChange("funder", e.target.value)}
          variant="outlined"
          size="small"
          fullWidth
          placeholder="Type to search funders..."
        />
        <TextField
          label="Status"
          value={filterInputs.status}
          onChange={(e) => handleFilterInputChange("status", e.target.value)}
          variant="outlined"
          size="small"
          fullWidth
          select
          SelectProps={{ native: true }}
        >
          <option value="">All Statuses</option>
          <option value="PENDING_OUTREACH">Pending Outreach</option>
          <option value="OUTREACH_SENT">Outreach Sent</option>
          <option value="REMINDER_1_SENT">Reminder 1 Sent</option>
          <option value="REMINDER_2_SENT">Reminder 2 Sent</option>
          <option value="ONBOARDED">Onboarded</option>
          <option value="CONNECTED">Connected</option>
          <option value="UNRESPONSIVE">Unresponsive</option>
        </TextField>
      </div>
    </Card>
  ), [filterInputs, handleFilterInputChange, handleClearFilters]);

  // Overview metrics cards - memoized to prevent re-creation
  const OverviewCards = useMemo(() => (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
      <Card>
        <div className="p-4 text-center">
          <Typography variant="h4" color="primary" style={{ fontWeight: 600 }}>
            {overviewMetrics.totalCampaigns}
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Total Campaigns
          </Typography>
        </div>
      </Card>
      <Card>
        <div className="p-4 text-center">
          <Typography variant="h4" color="primary" style={{ fontWeight: 600 }}>
            {overviewMetrics.totalOutreach}
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Total Outreach
          </Typography>
        </div>
      </Card>
      <Card>
        <div className="p-4 text-center">
          <Typography variant="h4" color="primary" style={{ fontWeight: 600 }}>
            {overviewMetrics.responsesReceived}
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Responses Received
          </Typography>
        </div>
      </Card>
      <Card>
        <div className="p-4 text-center">
          <Typography variant="h4" color="primary" style={{ fontWeight: 600 }}>
            {overviewMetrics.successfulConnections}
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Successful Connections
          </Typography>
        </div>
      </Card>
    </div>
  ), [overviewMetrics]);

  // Details dialog - memoized to prevent re-creation
  const DetailsDialog = useMemo(() => (
    <Dialog
      open={viewDialogOpen}
      onClose={() => setViewDialogOpen(false)}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>
        Outreach Details
        <Button
          style={{ float: "right" }}
          onClick={() => setViewDialogOpen(false)}
        >
          ×
        </Button>
      </DialogTitle>
      <DialogContent>
        {selectedRow && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
            <Box>
              <Typography variant="h6" gutterBottom>Campaign Information</Typography>
              <Typography variant="body2"><strong>Campaign ID:</strong> {selectedRow.campaign_id}</Typography>
              <Typography variant="body2"><strong>Campaign Name:</strong> {selectedRow.campaign_name || "N/A"}</Typography>
              <Typography variant="body2"><strong>Created:</strong> {selectedRow.created_at ? format(new Date(selectedRow.created_at), "MMM dd, yyyy HH:mm") : "N/A"}</Typography>
            </Box>
            <Box>
              <Typography variant="h6" gutterBottom>Founder Information</Typography>
              <Typography variant="body2"><strong>Name:</strong> {selectedRow.founder_name || "N/A"}</Typography>
              <Typography variant="body2"><strong>Email:</strong> {selectedRow.founder_email || "N/A"}</Typography>
            </Box>
            <Box>
              <Typography variant="h6" gutterBottom>Funder Information</Typography>
              <Typography variant="body2"><strong>Name:</strong> {(() => {
                const funderInfo = getFunderInfo(selectedRow);
                return funderInfo.name;
              })()}</Typography>
              <Typography variant="body2"><strong>Email:</strong> {(() => {
                const funderInfo = getFunderInfo(selectedRow);
                return funderInfo.email;
              })()}</Typography>
              <Typography variant="body2"><strong>Website:</strong> {(() => {
                const funderInfo = getFunderInfo(selectedRow);
                return funderInfo.website;
              })()}</Typography>
              <Typography variant="body2"><strong>Strategy:</strong> {(() => {
                const funderInfo = getFunderInfo(selectedRow);
                return funderInfo.strategy;
              })()}</Typography>
              <Typography variant="body2"><strong>Investment Amount:</strong> {(() => {
                const funderInfo = getFunderInfo(selectedRow);
                return funderInfo.investment_amount;
              })()}</Typography>
              <Typography variant="body2"><strong>Score:</strong> {selectedRow.score || "N/A"}</Typography>
            </Box>
            <Box>
              <Typography variant="h6" gutterBottom>Outreach Status</Typography>
              <Typography variant="body2"><strong>Status:</strong> <StatusChip status={selectedRow.status} /></Typography>
              <Typography variant="body2"><strong>Outreach Sent:</strong> {selectedRow.outreach_sent_at ? format(new Date(selectedRow.outreach_sent_at), "MMM dd, yyyy HH:mm") : "Not sent"}</Typography>
              {selectedRow.reminder_1_at && (
                <Typography variant="body2"><strong>Reminder 1:</strong> {format(new Date(selectedRow.reminder_1_at), "MMM dd, yyyy HH:mm")}</Typography>
              )}
              {selectedRow.reminder_2_at && (
                <Typography variant="body2"><strong>Reminder 2:</strong> {format(new Date(selectedRow.reminder_2_at), "MMM dd, yyyy HH:mm")}</Typography>
              )}
            </Box>
          </div>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setViewDialogOpen(false)} color="primary">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  ), [viewDialogOpen, selectedRow]);

  if (error) {
    return (
      <div className="p-8 text-center">
        <Typography variant="h6" color="error" gutterBottom>
          {error}
        </Typography>
        <Button variant="contained" onClick={() => window.location.reload()}>
          Retry
        </Button>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <div>
          <Typography variant="h4" component="h1" style={{ fontWeight: 600 }}>
            Funder Outreach Dashboard
          </Typography>
          {authUser?.roles?.includes('TENANT_ADMIN') && (
            <Typography variant="caption" color="textSecondary">
              Tenant Admin View - Showing data for Tenant ID: {authUser.tenantId}
            </Typography>
          )}
        </div>
        <Button
          variant="contained"
          color="primary"
          startIcon={<DownloadIcon />}
          onClick={handleExportCSV}
          disabled={outreachData.length === 0}
        >
          Export CSV
        </Button>
      </div>

      {OverviewCards}
      {FilterSection}

      <Card>
        <CardHeader>
          <span className="text-lg">Outreach Campaigns</span>
          <Typography variant="body2" color="textSecondary">
            {totalCount} total records
          </Typography>
        </CardHeader>
        
        <Table
          columns={columns}
          data={outreachData}
          loading={loading}
          error={error}
          page={page}
          rowsPerPage={rowsPerPage}
          totalCount={totalCount}
          onPageChange={(_, newPage) => setPage(newPage)}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          onRowClick={handleRowClick}
          stickyHeader
          className="border-0"
          emptyStateMessage="No outreach campaigns found"
        />
      </Card>

      {DetailsDialog}
    </div>
  );
};

export default FunderOutreach;
